import { Formik } from 'formik';
import Select, { MultiValue } from 'react-select';
import removeIcon from 'images/icons/svg/error.svg';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AuthService from 'services/AuthService';
import CompanyService from 'services/CompanyService';
import { CompanyModel, CreateMembershipModel, MembershipModel, RoleModel } from 'services/EpdClient';
import MembershipService from 'services/MembershipService';
import AdminService from 'services/admin/AdminService';
import { ButtonSmall, LabelBox, StyledReactSelect } from 'styles/Styles.styled';
import { Option } from 'types/types';
import { AdminGridForm, CompaniesListBox, CompaniesListItem, FixedLabel, ImageRemove } from './style';

interface Props {
  userid: string;
}

const EditUserMembership: React.FunctionComponent<Props> = ({ userid }) => {
  const { t } = useTranslation();
  const [companiesLookup, setCompaniesLookup] = useState<CompanyModel[]>([]);
  const [memberships, setMemberships] = useState<MembershipModel[]>([]);
  const [roles, setRoles] = useState<RoleModel[] | undefined>();

  const fetchUserMemberships = async () => {
    const result = await MembershipService.getMembershipsByUser(userid);
    setMemberships(result);
  };

  useEffect(() => {
    const fetchCompanies = async () => {
      const companiesResult = await CompanyService.getCompanies();
      setCompaniesLookup(companiesResult);
    };

    const loadRoles = async () => {
      const result = await AdminService.getRoles();
      setRoles(result);
    };

    fetchUserMemberships();
    fetchCompanies();
    loadRoles();
  }, [userid]);

  const initialValues = {
    newCompany: '',
  };

  const addCompany = async (company: Option) => {
    const defaultRole = roles?.find((x) => x.name === 'EPD Owner');
    const createModel: CreateMembershipModel = {
      companyId: company.value,
      userId: userid,
      roleId: defaultRole?.id,
    };
    await MembershipService.create(createModel);
    fetchUserMemberships();
  };

  const removeCompanyMemberships = async (m: MembershipModel[]) => {
    for (const element of m) {
      await MembershipService.deleteMembership(element.id);
    }

    fetchUserMemberships();
  };

  const handleRolesChange = async (companyId: string, selectedMemberships: MultiValue<Option>) => {
    const existingMemberships = memberships.filter((x) => x.companyId === companyId);
    const addedMemberships = selectedMemberships.filter((x) => !existingMemberships.some((y) => y.roleId === x.value));
    const deletedMemberships = existingMemberships.filter((x) => !selectedMemberships.some((y) => y.value === x.roleId));

    for (const element of addedMemberships) {
      await MembershipService.create({ companyId: companyId, userId: userid, roleId: element.value });
    }

    for (const element of deletedMemberships) {
      await MembershipService.deleteMembership(element.id);
    }

    fetchUserMemberships();
  };

  const withBreaks = (s: string | null) => s?.replaceAll('_', '_\u200B') ?? '';

  return (
    <Formik id={'formik2'} initialValues={initialValues} onSubmit={() => {}} enableReinitialize>
      {({ values, setFieldValue }) => (
        <AdminGridForm>
          <LabelBox>
            <FixedLabel>{t('account.companies')}</FixedLabel>
          </LabelBox>

          <FixedLabel style={{ marginLeft: '24px' }}>{t('account.roles')}</FixedLabel>

          <CompaniesListBox>
            {_.values(_.groupBy(memberships, (i) => i.companyId)).map((c) => {
              return (
                <CompaniesListItem key={c[0].companyId}>
                  <LabelBox>
                    <FixedLabel
                      style={{
                        whiteSpace: 'normal',
                        overflowWrap: 'normal',
                        wordBreak: 'keep-all',
                      }}
                    >
                      {withBreaks(c[0].companyName)}
                    </FixedLabel>
                  </LabelBox>
                  <Select
                    isDisabled={!AuthService.isAdmin()}
                    name={c[0].companyId}
                    onChange={(newOptions) => handleRolesChange(c[0].companyId, newOptions)}
                    options={roles?.map((r) => {
                      return { value: r.id!, label: r.name! } satisfies Option;
                    })}
                    defaultValue={c.map((t) => {
                      return { value: t.roleId, label: t.roleName as string } satisfies Option;
                    })}
                    isMulti
                  />
                  {AuthService.isAdmin() && (
                    <ImageRemove>
                      <img
                        src={removeIcon}
                        style={{ height: '1rem' }}
                        alt="Remove"
                        onClick={() => {
                          removeCompanyMemberships(c);
                        }}
                      />
                    </ImageRemove>
                  )}
                </CompaniesListItem>
              );
            })}
          </CompaniesListBox>
          {AuthService.isAdmin() && (
            <>
              <LabelBox>
                <FixedLabel>{t('account.connectToOrganization')}</FixedLabel>
              </LabelBox>
              <StyledReactSelect
                defaultValue={initialValues.newCompany}
                name="newCompany"
                options={companiesLookup?.map((company) => {
                  return { value: company.id, label: company.name } as Option;
                })}
                isClearable
              />
              <ButtonSmall
                style={{ padding: '0.5rem 1rem', borderRadius: '3px' }}
                type="button"
                disabled={values.newCompany == null}
                onClick={() => {
                  addCompany(values.newCompany as any);
                  setFieldValue('newCompany', initialValues.newCompany);
                }}
              >
                +
              </ButtonSmall>
            </>
          )}
        </AdminGridForm>
      )}
    </Formik>
  );
};

export default EditUserMembership;
